/**
 * Sync Service - Manages online/offline synchronization
 * Uses IndexedDB for local storage and batch sync endpoints
 */

import api from './api';
import {
  initOfflineDB,
  applyProductSnapshot,
  cacheRecentCustomers,
  cacheRefillSchedule,
  getPendingSales,
  markSaleSynced,
  markSaleFailed,
  getPendingProducts,
  markProductSynced,
  markProductFailed,
  clearSyncedProducts,
  getPendingPurchases,
  markPurchaseSynced,
  markPurchaseFailed,
  clearSyncedPurchases,
  getPendingAdrReports,
  markAdrReportSynced,
  markAdrReportFailed,
  clearSyncedAdrReports,
  saveSetting,
  getSetting,
  logSync,
  getOfflineStatus,
  clearSyncedSales,
} from './offlineDB';
import { syncQueuedRecallReturns } from './offlineRecallReturns';
import { syncQueuedPulls } from './offlineRecallPulls';
import { getOfflineBranchId, assertOfflineBranch, OFFLINE_CACHE_VERSION } from './offlineScope';

/**
 * Best-effort hint of the effective branch for the signed-in user, read
 * straight from the cached auth profile — mirrors the backend's
 * resolve_offline_branch_id() so we know whether a previous delta-sync
 * watermark still applies to the currently selected branch.
 */
const getEffectiveBranchHint = () => {
  return getOfflineBranchId();
};

const PRODUCT_CACHE_SCHEMA = OFFLINE_CACHE_VERSION;
const RECALL_REFRESH_MS = 10 * 60 * 1000;
export const FULL_REFRESH_MS = 2 * 60 * 60 * 1000;

let syncInProgress = false;
let syncListeners = [];
let refreshTimer = null;
let productSyncPromise = null;
let fullSyncPromise = null;
let initialized = false;

const recordSyncError = async (branchId, message) => {
  try { await saveSetting('last_offline_sync_error', { branch_id: branchId, message, at: new Date().toISOString() }); } catch { /* Keep the original failure. */ }
};

// ==================== EVENT HANDLING ====================

export const addSyncListener = (callback) => {
  syncListeners.push(callback);
  return () => {
    syncListeners = syncListeners.filter(cb => cb !== callback);
  };
};

const notifyListeners = (event, data) => {
  syncListeners.forEach(cb => {
    try { cb(event, data); } catch (e) { console.error('Sync listener error:', e); }
  });
};

// ==================== PRODUCT SYNC ====================

/**
 * Download and cache products for offline use via /products/offline endpoint
 */
/**
 * Download and cache products for offline use via /products/offline.
 * Uses a delta sync (only changed/deleted products) when the last sync's
 * watermark and branch still match; otherwise falls back to a full snapshot.
 */
export const syncProducts = (onProgress) => {
  if (!productSyncPromise) productSyncPromise = syncProductsOnce(onProgress).finally(() => {
    productSyncPromise = null;
    notifyListeners('cache_sync_idle', null);
  });
  return productSyncPromise;
};

const syncProductsOnce = async (onProgress) => {
  const branchHint = getEffectiveBranchHint();
  try {
    notifyListeners('products_sync_start', null);
    onProgress?.({ phase: 'products', status: 'downloading' });

    const watermark = await getSetting('last_product_sync_watermark');
    const cachedBranchId = await getSetting('cached_branch_id');
    const schema = await getSetting('product_cache_schema');
    assertOfflineBranch(branchHint);
    // A cache built before recall flags/batches were included must be rebuilt in full once.
    const canDelta = !!(watermark && cachedBranchId && branchHint && cachedBranchId === branchHint
      && schema === PRODUCT_CACHE_SCHEMA);

    const response = await api.get('/products/offline', { params: canDelta ? { since: watermark } : {}, timeout: 30000 });
    const { products, deleted_product_ids, is_delta, branch_id, branch_name, timestamp, frequent_product_ids } = response.data;

    if (branch_id !== branchHint || !Array.isArray(products) || !Array.isArray(frequent_product_ids) || !timestamp) {
      throw new Error('The offline snapshot was incomplete or belongs to another branch.');
    }
    const cachedCount = await applyProductSnapshot(response.data, () => assertOfflineBranch(branchHint));
    assertOfflineBranch(branchHint);
    await logSync('products_sync', { count: cachedCount, is_delta: !!is_delta, deleted: deleted_product_ids?.length || 0 });

    notifyListeners('products_sync_complete', { count: cachedCount });
    onProgress?.({ phase: 'products', status: 'done', count: cachedCount });

    return { success: true, count: cachedCount, is_delta: !!is_delta };
  } catch (error) {
    console.error('Product sync failed:', error);
    await recordSyncError(branchHint, error.response?.data?.detail || error.message || 'Product refresh failed');
    notifyListeners('products_sync_error', { error: error.message });
    throw error;
  }
};

// ==================== RECENT CUSTOMERS SYNC ====================

/**
 * Download and cache recent customers for offline lookup via /sync/recent-customers
 */
export const syncRecentCustomers = async (branchId = getEffectiveBranchHint()) => {
  try {
    assertOfflineBranch(branchId);
    const response = await api.get('/sync/recent-customers', { timeout: 30000 });
    const { customers, count } = response.data;
    if (response.data.branch_id !== branchId || !Array.isArray(customers)) throw new Error('Customer snapshot is incomplete or belongs to another branch');
    await cacheRecentCustomers(customers, branchId, () => assertOfflineBranch(branchId));
    return { success: true, count };
  } catch (error) {
    console.error('Recent customers sync failed:', error);
    return { success: false, count: 0 };
  }
};

/**
 * Download and cache the refill-eligibility schedule for offline POS
 * dispensing checks via /sync/refill-schedule
 */
export const syncRefillSchedule = async (branchId = getEffectiveBranchHint()) => {
  try {
    assertOfflineBranch(branchId);
    const response = await api.get('/sync/refill-schedule', { timeout: 30000 });
    const { schedule, count } = response.data;
    if (response.data.branch_id !== branchId || !Array.isArray(schedule)) throw new Error('Refill snapshot is incomplete or belongs to another branch');
    await cacheRefillSchedule(schedule, branchId, () => assertOfflineBranch(branchId));
    return { success: true, count };
  } catch (error) {
    console.error('Refill schedule sync failed:', error);
    return { success: false, count: 0 };
  }
};

// ==================== SALES SYNC ====================

/**
 * Sync pending offline sales to server via batch /sync/sales endpoint
 */
export const syncPendingSales = async (onProgress) => {
  if (syncInProgress) {
    return { skipped: true };
  }

  syncInProgress = true;
  notifyListeners('sales_sync_start', null);

  try {
    const pendingSales = await getPendingSales();

    if (pendingSales.length === 0) {
      syncInProgress = false;
      notifyListeners('sales_sync_complete', { synced: 0, failed: 0 });
      return { synced: 0, failed: 0 };
    }

    onProgress?.({ phase: 'sales', status: 'uploading', total: pendingSales.length });

    // Prepare batch payload
    const salesPayload = pendingSales.map(sale => ({
      offline_id: sale.offline_id,
      items: sale.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        unit_cost: item.unit_cost || 0,
        batch_number: item.batch_number || '',
      })),
      subtotal: sale.subtotal,
      discount: sale.discount || 0,
      total: sale.total,
      payment_method: sale.payment_method,
      cash_received: sale.cash_received,
      change_due: sale.change_due,
      customer_name: sale.customer_name || '',
      customer_phone: sale.customer_phone || '',
      notes: sale.notes || '',
      prescription_info: sale.prescription_info || null,
      created_at: sale.created_at,
    }));

    const response = await api.post('/sync/sales', { sales: salesPayload });
    const { results, summary } = response.data;

    // Update local records based on server response
    for (const result of results) {
      const localSale = pendingSales.find(s => s.offline_id === result.offline_id);
      if (!localSale) continue;

      if (result.status === 'synced' || result.status === 'already_synced') {
        await markSaleSynced(localSale.local_id, result.sale_id);
      } else if (result.status === 'failed') {
        await markSaleFailed(localSale.local_id, result.error || 'Server sync failed');
      }
    }

    // Stock conflicts are never silently dropped — they're recorded server-side
    // and surfaced via GET /sync/conflicts for a manager to glance over.
    if (summary?.conflicts > 0) {
      notifyListeners('sales_sync_conflicts', { count: summary.conflicts });
    }

    await clearSyncedSales();
    await logSync('sales_sync', summary);
    await saveSetting('last_sales_sync', new Date().toISOString());

    notifyListeners('sales_sync_complete', summary);
    onProgress?.({ phase: 'sales', status: 'done', ...summary });

    return summary;
  } catch (error) {
    console.error('Sales sync failed:', error);
    notifyListeners('sales_sync_error', { error: error.message });
    // Don't throw - let the caller handle gracefully
    return { synced: 0, failed: 0, error: error.message };
  } finally {
    syncInProgress = false;
  }
};

// ==================== NEW ITEMS / PURCHASES / ADR REPORTS SYNC ====================
// Each of these queues only ever *creates* a record server-side, so there's
// no stock-conflict logic to replay — just "send it, mark it synced".

const queueSyncLocks = new Set();

const syncGenericQueue = async ({ getPending, mark, clearSynced, endpoint, wrapKey, idKey, event }) => {
  if (queueSyncLocks.has(endpoint)) return { synced: 0, failed: 0, total: 0, skipped: true };
  queueSyncLocks.add(endpoint);
  try {
    const all = await getPending();
    // Only push rows created under the branch currently selected on this
    // terminal — an admin who queued something for Branch A, then switched
    // to Branch B, shouldn't have it silently created under B.
    const branchHint = getEffectiveBranchHint();
    const pending = all.filter((row) => !row.branch_id || row.branch_id === branchHint);
    if (pending.length === 0) return { synced: 0, failed: 0, total: 0 };

    const payload = pending.map(({ offline_id, local_id, status, sync_attempts, last_error, last_attempt_at, server_id, synced_at, branch_id, ...rest }) => ({ ...rest, offline_id }));
    const { data } = await api.post(endpoint, { [wrapKey]: payload });
    for (const result of data.results || []) {
      const local = pending.find(p => p.offline_id === result.offline_id);
      if (!local) continue;
      if (result.status === 'synced' || result.status === 'already_synced') await mark.synced(local.local_id, result[idKey]);
      else if (result.status === 'failed') await mark.failed(local.local_id, result.error || 'Server sync failed');
    }
    await clearSynced();
    const summary = data.summary || {};
    notifyListeners(event, summary); // refresh any "N waiting to sync" badge even on a no-op (all already_synced) run
    return summary;
  } catch (error) {
    console.error(`${event} failed:`, error);
    return { synced: 0, failed: 0, error: error.message };
  } finally {
    queueSyncLocks.delete(endpoint);
  }
};

export const syncPendingProducts = () => syncGenericQueue({
  getPending: getPendingProducts,
  mark: { synced: markProductSynced, failed: markProductFailed },
  clearSynced: clearSyncedProducts,
  endpoint: '/sync/products', wrapKey: 'products', idKey: 'product_id', event: 'products_queue_synced',
});

export const syncPendingPurchases = () => syncGenericQueue({
  getPending: getPendingPurchases,
  mark: { synced: markPurchaseSynced, failed: markPurchaseFailed },
  clearSynced: clearSyncedPurchases,
  endpoint: '/sync/purchases', wrapKey: 'receipts', idKey: 'po_id', event: 'purchases_queue_synced',
});

export const syncPendingAdrReports = () => syncGenericQueue({
  getPending: getPendingAdrReports,
  mark: { synced: markAdrReportSynced, failed: markAdrReportFailed },
  clearSynced: clearSyncedAdrReports,
  endpoint: '/sync/adr-reports', wrapKey: 'reports', idKey: 'report_id', event: 'adr_reports_queue_synced',
});

/** Fire-and-forget: push every "created while offline" queue in one go. */
const syncCreationQueues = async () => {
  await Promise.all([syncPendingProducts(), syncPendingPurchases(), syncPendingAdrReports()]);
};

// ==================== FULL SYNC ====================

/**
 * Perform full sync (pending sales first, then refresh product cache)
 */
export const performFullSync = (onProgress) => {
  if (!fullSyncPromise) fullSyncPromise = performFullSyncOnce(onProgress).finally(() => {
    fullSyncPromise = null;
    notifyListeners('cache_sync_idle', null);
  });
  return fullSyncPromise;
};

const performFullSyncOnce = async (onProgress) => {
  const branchId = getEffectiveBranchHint();
  try {
    notifyListeners('full_sync_start', null);
    assertOfflineBranch(branchId);
    const cachedBranch = await getSetting('cached_branch_id');
    if (cachedBranch && cachedBranch !== branchId && (await getPendingSales()).length) {
      throw new Error('Return to the previous branch and sync its pending sales before preparing this branch.');
    }

    // Step 1: Sync pending sales first (preserve offline data)
    const salesResult = await syncPendingSales(onProgress);
    if (salesResult.error || salesResult.failed || salesResult.skipped || (await getPendingSales()).length) {
      throw new Error('Pending sales could not be synced. The existing offline stock has been kept.');
    }
    const recallReturnsResult = await syncRecallReturns();
    await syncCreationQueues();

    // Step 2: Download fresh product + recent-customer + refill-eligibility data
    const productsResult = await syncProducts(onProgress);
    assertOfflineBranch(branchId);
    const customersResult = await syncRecentCustomers(branchId);
    const refillResult = await syncRefillSchedule(branchId);
    assertOfflineBranch(branchId);
    const successful = customersResult.success && refillResult.success && !recallReturnsResult.failed;
    const completedAt = new Date().toISOString();
    if (successful) {
      await saveSetting('last_complete_offline_sync', { branch_id: branchId, at: completedAt, version: OFFLINE_CACHE_VERSION });
      await saveSetting('last_sync', completedAt);
      await saveSetting('last_offline_sync_error', null);
    } else {
      const missing = [!customersResult.success && 'customers', !refillResult.success && 'refill rules', recallReturnsResult.failed && 'recall returns'].filter(Boolean);
      await recordSyncError(branchId, `Products refreshed, but ${missing.join(' and ')} could not sync. Please retry.`);
    }

    const result = {
      success: successful,
      sales: salesResult,
      products: productsResult,
      customers: customersResult,
      refills: refillResult,
      recall_returns: recallReturnsResult,
      timestamp: new Date().toISOString(),
    };

    notifyListeners('full_sync_complete', result);
    return result;
  } catch (error) {
    await recordSyncError(branchId, error.response?.data?.detail || error.message || 'Offline refresh failed');
    notifyListeners('full_sync_error', { error: error.message });
    throw error;
  }
};

// ==================== INITIALIZATION ====================

/**
 * Initialize sync service - call once at app startup
 */
export const initSyncService = async () => {
  await initOfflineDB();
  if (!initialized) {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    initialized = true;
  }

  const status = await getOfflineStatus();
  notifyListeners('init', status);
  if (!refreshTimer) refreshTimer = setInterval(refreshOfflineCache, RECALL_REFRESH_MS);
  if (navigator.onLine) refreshOfflineCache();
  return status;
};

const syncRecallReturns = async () => {
  try {
    const res = await syncQueuedRecallReturns();
    if (res.synced || res.failed) notifyListeners('recall_returns_synced', res);
    return res;
  } catch (error) {
    console.error('Recall return sync failed:', error);
    return { synced: 0, failed: 0 };
  }
};

// Full refresh every 2h (from first sign-in) plus 10-min recall/product deltas while online.
const refreshOfflineCache = async () => {
  if (!navigator.onLine || !getEffectiveBranchHint() || fullSyncPromise) return;
  try {
    await syncCreationQueues();
    await syncQueuedPulls().catch(() => 0);
    const branch = getEffectiveBranchHint();
    const last = await getSetting('last_complete_offline_sync');
    const lastAt = last?.branch_id === branch ? Date.parse(last.at) : NaN;
    const status = await getOfflineStatus();
    if (!(Date.now() - lastAt < FULL_REFRESH_MS) || status.needs_refresh || status.last_error) {
      await performFullSync();
      return;
    }
    if ((await getPendingSales()).length) return;
    await syncRecallReturns();
    await syncProducts();
  } catch (error) {
    console.error('Background cache refresh failed:', error);
  }
};

const handleOnline = async () => {
  console.log('Connection restored - starting auto-sync');
  notifyListeners('online', null);

  try {
    await syncPendingSales();
    await syncCreationQueues();
    await refreshOfflineCache();
  } catch (error) {
    console.error('Auto-sync failed:', error);
  }
};

const handleOffline = () => {
  console.log('Connection lost - offline mode active');
  notifyListeners('offline', null);
};

// ==================== STATUS ====================

/**
 * Get current sync status
 */
export const getSyncStatus = async () => {
  const status = await getOfflineStatus();
  return {
    ...status,
    is_online: navigator.onLine,
    sync_in_progress: syncInProgress,
    cache_sync_in_progress: !!(fullSyncPromise || productSyncPromise),
  };
};

/**
 * Name of the single branch whose products/stock are currently cached
 * offline on this terminal (set by the last successful product sync).
 */
export const getCachedBranchName = async () => getSetting('cached_branch_name');

export default {
  initSyncService,
  syncProducts,
  syncRecentCustomers,
  syncRefillSchedule,
  syncPendingSales,
  syncPendingProducts,
  syncPendingPurchases,
  syncPendingAdrReports,
  performFullSync,
  getSyncStatus,
  getCachedBranchName,
  addSyncListener,
};
